import { useEffect, useState } from 'react';

/**
 * Container-driven compact mode (#517, the #142/#231 ResizeObserver pattern from
 * FE/DESIGN_SYSTEM.md): `compact` is true while the element given to `ref` is
 * narrower than `threshold` px. A chat card's width follows its CONTAINER (split
 * view, a narrow chat column), not the viewport, so a media query can't tell.
 *
 * Usage: `const [cardRef, compact] = useCompactWidth(320); <div ref={cardRef}>`.
 * The ref is a callback ref, so an element that mounts later (or is replaced)
 * is observed too.
 *
 * Without ResizeObserver (jsdom, very old browsers) or before the first
 * measurement it reports false — the layout still wraps (flex-wrap); compact
 * only trims labels to icons.
 *
 * @param {number} threshold width in px below which the element is "compact"
 * @returns {[(el: Element|null) => void, boolean]}
 */
export default function useCompactWidth(threshold = 320) {
    const [el, setEl] = useState(null);
    const [compact, setCompact] = useState(false);
    useEffect(() => {
        if (!el || typeof ResizeObserver === 'undefined') return undefined;
        const measure = (width) => {
            if (width > 0) setCompact(width < threshold);
        };
        measure(el.getBoundingClientRect?.().width || 0);
        const ro = new ResizeObserver((entries) => {
            const entry = entries[entries.length - 1];
            measure(entry?.contentRect?.width || 0);
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, [el, threshold]);
    return [setEl, compact];
}
